'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { Input } from '@savvy-kit/components/input';
import { Label } from '@savvy-kit/components/label';

const MAX_NAME_LENGTH = 50;

type SaveViewDialogLabels = {
  readonly title: string;
  readonly fieldLabel: string;
  readonly placeholder: string;
  readonly cancel: string;
  readonly save: string;
  readonly requiredError: string;
  readonly maxLengthError: (maxLength: number) => string;
  readonly duplicateError: string;
};

export const DEFAULT_SAVE_VIEW_DIALOG_LABELS: SaveViewDialogLabels = {
  title: 'Save filter as view',
  fieldLabel: 'Name',
  placeholder: 'e.g. My open tasks',
  cancel: 'Cancel',
  save: 'Save',
  requiredError: 'Name is required.',
  maxLengthError: (maxLength) => `Name must be ${maxLength} characters or fewer.`,
  duplicateError: 'A view with this name already exists.',
};

type SaveViewDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (name: string) => void;
  readonly initialValue?: string;
  readonly existingNames: string[];
  /**
   * Per-consumer overrides for any subset of the dialog copy. Unspecified
   * keys fall back to {@link DEFAULT_SAVE_VIEW_DIALOG_LABELS}. Accept
   * `Partial` so callers can override just the placeholder (the most common
   * need — the default `e.g. My open tasks` is task-specific) without
   * having to restate the rest of the copy.
   */
  readonly labels?: Partial<SaveViewDialogLabels>;
};

export function SaveViewDialog({
  open,
  onOpenChange,
  onSubmit,
  initialValue = '',
  existingNames,
  labels: labelOverrides,
}: SaveViewDialogProps) {
  const labels: SaveViewDialogLabels = labelOverrides
    ? { ...DEFAULT_SAVE_VIEW_DIALOG_LABELS, ...labelOverrides }
    : DEFAULT_SAVE_VIEW_DIALOG_LABELS;
  const [name, setName] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  function handleChange(value: string) {
    setName(value);
    setError(null);
  }

  function handleSubmit() {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(labels.requiredError);
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setError(labels.maxLengthError(MAX_NAME_LENGTH));
      return;
    }
    const isDuplicate = existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase() && n !== initialValue);
    if (isDuplicate) {
      setError(labels.duplicateError);
      return;
    }
    onSubmit(trimmed);
    onOpenChange(false);
  }

  const isValid = name.trim().length > 0 && name.trim().length <= MAX_NAME_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="save-view-name">{labels.fieldLabel}</Label>
          <Input
            id="save-view-name"
            ref={inputRef}
            value={name}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isValid) handleSubmit();
              if (e.key === 'Escape') onOpenChange(false);
            }}
            placeholder={labels.placeholder}
            maxLength={MAX_NAME_LENGTH}
            aria-invalid={error !== null}
            aria-describedby={error !== null ? 'save-view-error' : undefined}
            autoComplete="off"
            autoFocus
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          {error !== null && (
            <p id="save-view-error" role="alert" className="text-sm text-[var(--destructive)]">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {labels.cancel}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            {labels.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type { SaveViewDialogLabels };
